/**
 * 验证工具函数
 *
 * 提供各种输入验证和安全清理功能，防止XSS、注入攻击、DoS等安全风险
 */

// ==================== 常量定义 ====================

export const MAX_FILE_SIZE = 5 * 1024 * 1024;
export const MAX_JSON_LENGTH = 5 * 1024 * 1024;
export const MAX_NODES = 100;
export const MAX_EDGES = 200;
export const MAX_STRING_LENGTH = 10000;
export const MAX_ID_LENGTH = 100;
export const NODE_POSITION_RANGE = { min: -10000, max: 10000 };
export const TOP_K_RANGE = { min: 1, max: 100 };
export const SIMILARITY_RANGE = { min: 0, max: 1 };

const DANGEROUS_KEYS = ['__proto__', 'constructor', 'prototype'];
const MAX_NESTING_DEPTH = 20;

// ==================== 工具函数 ====================

/**
 * 递归检测对象深度，防止深度嵌套攻击
 */
const getObjectDepth = (obj: any, depth: number = 0): number => {
  if (depth > MAX_NESTING_DEPTH) return depth;
  if (typeof obj !== 'object' || obj === null) return depth;

  // 使用栈代替递归，避免栈溢出（性能优化）
  const stack: Array<{ obj: any; depth: number }> = [{ obj, depth }];
  let maxDepth = depth;

  while (stack.length > 0) {
    const { obj: currentObj, depth: currentDepth } = stack.pop()!;

    if (currentDepth > maxDepth) {
      maxDepth = currentDepth;
    }

    if (currentDepth > MAX_NESTING_DEPTH) {
      continue;
    }

    for (const key in currentObj) {
      if (Object.prototype.hasOwnProperty.call(currentObj, key)) {
        const value = currentObj[key];
        if (typeof value === 'object' && value !== null) {
          stack.push({ obj: value, depth: currentDepth + 1 });
        }
      }
    }
  }

  return maxDepth;
};

/**
 * 递归检测原型污染
 */
const hasPrototypePollution = (obj: any, depth: number = 0): boolean => {
  if (depth > MAX_NESTING_DEPTH) return true; // 超过最大深度，视为污染

  if (typeof obj !== 'object' || obj === null) return false;

  // 检查当前层级是否有危险键
  if (DANGEROUS_KEYS.some(key => key in obj)) {
    return true;
  }

  // 使用栈代替递归，避免栈溢出（性能优化）
  const stack: Array<{ obj: any; depth: number }> = [{ obj, depth }];

  while (stack.length > 0) {
    const { obj: currentObj, depth: currentDepth } = stack.pop()!;

    if (currentDepth > MAX_NESTING_DEPTH) {
      return true;
    }

    if (DANGEROUS_KEYS.some(key => key in currentObj)) {
      return true;
    }

    for (const key in currentObj) {
      if (Object.prototype.hasOwnProperty.call(currentObj, key)) {
        const value = currentObj[key];
        if (typeof value === 'object' && value !== null) {
          stack.push({ obj: value, depth: currentDepth + 1 });
        }
      }
    }
  }

  return false;
};

export const sanitizeString = (str: any): string => {
  if (typeof str !== 'string') return '';
  // 优化：使用正则表达式缓存（浏览器会自动缓存常用正则）
  const cleaned = str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  return cleaned.length > MAX_STRING_LENGTH ? cleaned.substring(0, MAX_STRING_LENGTH) : cleaned;
};

const inRange = (num: number, min: number, max: number): boolean => !isNaN(num) && num >= min && num <= max;

// ==================== JSON验证 ====================

export const isValidJSON = (jsonString: string): boolean => {
  if (jsonString.length > MAX_JSON_LENGTH) return false;
  try {
    JSON.parse(jsonString);
    return true;
  } catch {
    return false;
  }
};

export const safeParseJSON = <T = any>(jsonString: string): T => {
  // 优化：组合检查，减少字符串遍历次数
  const dangerousKeyPattern = /["'](__proto__|constructor|prototype)["']/;
  if (dangerousKeyPattern.test(jsonString)) {
    throw new Error(`JSON包含非法属性`);
  }

  const parsed = JSON.parse(jsonString, (key, value) => {
    // 过滤危险键
    if (DANGEROUS_KEYS.includes(key)) return undefined;
    return value;
  });

  // 递归检测原型污染
  if (hasPrototypePollution(parsed)) {
    throw new Error('JSON包含原型污染风险');
  }

  // 检测对象深度
  if (getObjectDepth(parsed) > MAX_NESTING_DEPTH) {
    throw new Error('JSON对象嵌套深度超过限制');
  }

  return parsed;
};

// ==================== 字符串验证 ====================

/**
 * 转义HTML特殊字符，防止XSS攻击
 */
export const escapeHtml = (str: any): string => {
  if (typeof str !== 'string') return '';
  const htmlEscapes: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;'
  };
  return str.replace(/[&<>"'/]/g, (char) => htmlEscapes[char]);
};

export const sanitizeNodeId = (id: string): string => {
  const cleaned = id.replace(/[^a-zA-Z0-9\-_]/g, '');
  return cleaned.substring(0, MAX_ID_LENGTH);
};

export const sanitizeNumber = (num: any, min: number, max: number, defaultValue: number = min): number => {
  const parsed = Number(num);
  return inRange(parsed, min, max) ? parsed : defaultValue;
};

export const sanitizeNumberArray = (arr: any, min: number = 1, max: number = 10000): number[] => {
  if (!Array.isArray(arr)) return [];
  return arr.map(n => sanitizeNumber(n, min, max)).filter(n => n > 0);
};

// ==================== 文件验证 ====================

export const validateFileSize = (file: File): boolean => file.size <= MAX_FILE_SIZE;
export const validateFileType = (file: File, allowedTypes: string[] = ['application/json']): boolean => allowedTypes.includes(file.type);

// ==================== 错误信息清理 ====================

export const sanitizeErrorMessage = (error: any, defaultMessage: string = '操作失败'): string => {
  if (error instanceof Error) {
    const message = error.message;
    return message.length > 200 ? message.substring(0, 200) + '...' : message;
  }
  if (typeof error === 'string') {
    // 优化：使用正则表达式替换多个特殊字符
    const cleaned = error.replace(/[<>]/g, '');
    return cleaned.length > 200 ? cleaned.substring(0, 200) + '...' : cleaned;
  }
  return defaultMessage;
};

// ==================== 认证输入验证 ====================

// 预编译正则表达式，提升性能
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX = /^(?=.*[a-zA-Z])(?=.*\d).{9,}$/;

// 验证错误消息常量
const VALIDATION_ERROR_MESSAGES = {
  EMAIL_EMPTY: '邮箱不能为空',
  EMAIL_INVALID: '邮箱格式不正确',
  PASSWORD_EMPTY: '密码不能为空',
  PASSWORD_TOO_SHORT: '密码必须大于8位',
  PASSWORD_INVALID: '密码必须包含字母和数字',
  PASSWORD_MISMATCH: '两次输入的密码不一致',
} as const;

/**
 * 验证邮箱输入（组合验证：非空 + 格式）
 * 优化：一次函数调用完成所有邮箱验证，减少函数调用开销
 */
const validateEmailInput = (email: string): { valid: boolean; message?: string } => {
  // 优化：提前修剪和空值检查，减少条件分支
  const trimmedEmail = email?.trim();

  if (!trimmedEmail) {
    return { valid: false, message: VALIDATION_ERROR_MESSAGES.EMAIL_EMPTY };
  }

  if (!EMAIL_REGEX.test(trimmedEmail)) {
    return { valid: false, message: VALIDATION_ERROR_MESSAGES.EMAIL_INVALID };
  }

  return { valid: true };
};

/**
 * 验证密码输入（组合验证：非空 + 长度 + 复杂度）
 * 优化：一次函数调用完成所有密码验证，减少函数调用开销
 */
const validatePasswordInput = (password: string): { valid: boolean; message?: string } => {
  // 优化：提前空值和长度检查，避免不必要的正则匹配
  if (!password || !password.trim()) {
    return { valid: false, message: VALIDATION_ERROR_MESSAGES.PASSWORD_EMPTY };
  }

  const len = password.length;

  // 快速长度检查（在正则之前）
  if (len <= 8) {
    return { valid: false, message: VALIDATION_ERROR_MESSAGES.PASSWORD_TOO_SHORT };
  }

  if (!PASSWORD_REGEX.test(password)) {
    return { valid: false, message: VALIDATION_ERROR_MESSAGES.PASSWORD_INVALID };
  }

  return { valid: true };
};

/**
 * 验证邮箱格式（单独的格式验证函数，供外部使用）
 */
export const validateEmail = (email: string): boolean => {
  return EMAIL_REGEX.test(email);
};

/**
 * 验证密码格式（单独的格式验证函数，供外部使用）
 */
export const validatePassword = (password: string): boolean => {
  return PASSWORD_REGEX.test(password);
};

/**
 * 验证登录输入
 */
export const validateLoginInput = (email: string, password: string): { valid: boolean; message?: string } => {
  const emailResult = validateEmailInput(email);
  if (!emailResult.valid) return emailResult;

  const passwordResult = validatePasswordInput(password);
  return passwordResult;
};

/**
 * 验证注册输入
 */
export const validateRegisterInput = (
  email: string,
  password: string,
  confirmPassword: string
): { valid: boolean; message?: string } => {
  const emailResult = validateEmailInput(email);
  if (!emailResult.valid) return emailResult;

  const passwordResult = validatePasswordInput(password);
  if (!passwordResult.valid) return passwordResult;

  if (password !== confirmPassword) {
    return { valid: false, message: VALIDATION_ERROR_MESSAGES.PASSWORD_MISMATCH };
  }

  return { valid: true };
};

/**
 * 认证错误消息映射（常量，避免重复创建）
 * 优化：使用常量Map，避免每次调用都创建新对象
 */
const AUTH_ERROR_MAP = new Map<string, string>([
  ['INVALID_CREDENTIALS', '邮箱或密码错误'],
  ['邮箱或密码错误', '邮箱或密码错误'],
  ['EMAIL_EXISTS', '该邮箱已被注册'],
  ['邮箱已存在', '该邮箱已被注册'],
  ['INVALID_EMAIL', '邮箱格式不正确'],
  ['邮箱格式无效', '邮箱格式不正确'],
  ['PASSWORD_TOO_SHORT', '密码必须大于8位'],
  ['密码必须大于8位', '密码必须大于8位'],
  ['PASSWORD_MISMATCH', '两次输入的密码不一致'],
  ['两次输入的密码不一致', '两次输入的密码不一致'],
  ['network', '网络错误，请检查网络连接'],
  ['fetch', '网络错误，请检查网络连接'],
  ['网络错误', '网络错误，请检查网络连接'],
  ['超时', '请求超时，请稍后重试'],
  ['timeout', '请求超时，请稍后重试'],
]);

export const getAuthErrorMessage = (error: any, _mode: 'login' | 'register' = 'login'): string => {
  const message = typeof error === 'string' ? error : error instanceof Error ? error.message : '';

  // 使用常量Map提高查找性能（O(1)）
  for (const [key, value] of AUTH_ERROR_MAP) {
    if (message.includes(key)) return value;
  }

  return message || '操作失败，请稍后重试';
};

export const validateToken = (token: string): boolean => {
  if (!token || !token.trim()) return false;
  // JWT token basic structure validation (header.payload.signature)
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  // Each part should be a valid base64url string
  const base64Regex = /^[A-Za-z0-9_-]+$/;
  return parts.every(part => base64Regex.test(part));
};

// ==================== 工作区验证 ====================

export const validateWorkspaceName = (name: string): { valid: boolean; message?: string } => {
  if (!name || !name.trim()) {
    return { valid: false, message: '工作区名称不能为空' };
  }
  
  const trimmedName = name.trim();
  
  if (trimmedName.length > 20) {
    return { valid: false, message: '工作区名称长度不能超过 20 字符' };
  }
  
  const hasChinese = /[\u4e00-\u9fff]/.test(trimmedName);
  if (!hasChinese) {
    return { valid: false, message: '工作区名称必须包含至少一个汉字' };
  }
  
  return { valid: true };
};

export const validateChapterConfig = (
  chapterNums: number,
  wordsPerChapter: number
): { valid: boolean; message?: string } => {
  if (!Number.isInteger(chapterNums) || chapterNums < 1 || chapterNums > 10000) {
    return { valid: false, message: '章节数量必须是1-10000之间的整数' };
  }
  if (!Number.isInteger(wordsPerChapter) || wordsPerChapter < 100 || wordsPerChapter > 100000) {
    return { valid: false, message: '每章字数必须是100-100000之间的整数' };
  }
  return { valid: true };
};

export const validateWorkspaceCreate = (
  name: string,
  embeddingApiUrl: string,
  chapterNums?: number,
  wordsPerChapter?: number
): { valid: boolean; message?: string } => {
  // 验证工作区名称
  const nameResult = validateWorkspaceName(name);
  if (!nameResult.valid) return nameResult;

  // 验证嵌入模型 API URL（必须配置）
  if (!embeddingApiUrl || !embeddingApiUrl.trim()) {
    return { valid: false, message: '必须配置知识库嵌入模型方可创建工作区' };
  }

  // 验证 URL 格式
  try {
    new URL(embeddingApiUrl);
  } catch {
    return { valid: false, message: '嵌入模型 API URL 格式不正确' };
  }

  // 如果提供了章节配置，验证章节配置
  if (chapterNums !== undefined && wordsPerChapter !== undefined) {
    const chapterResult = validateChapterConfig(chapterNums, wordsPerChapter);
    if (!chapterResult.valid) return chapterResult;
  }

  return { valid: true };
};

// ==================== 设定 Token 限制验证 ====================

export const SETTING_TOKEN_LIMITS = {
  MAX_TOTAL_TOKENS: 128000,
  MAX_SINGLE_SETTING_TOKENS: 40000,
  MAX_SINGLE_SETTING_CHARS: 50000,
};

/**
 * 验证单个设定内容的字符数
 */
export const validateSettingContent = (content: string): { valid: boolean; message?: string } => {
  if (!content || !content.trim()) {
    return { valid: false, message: '设定内容不能为空' };
  }

  if (content.length > SETTING_TOKEN_LIMITS.MAX_SINGLE_SETTING_CHARS) {
    return {
      valid: false,
      message: `设定内容长度不能超过 ${SETTING_TOKEN_LIMITS.MAX_SINGLE_SETTING_CHARS} 字符`
    };
  }

  return { valid: true };
};

// ==================== 智能体名称验证 ====================

/**
 * 验证智能体名称：不能超过 5 个汉字或 10 个英文字母
 */
export const validateAgentName = (name: string): { valid: boolean; message?: string } => {
  if (!name || !name.trim()) {
    return { valid: false, message: '智能体名称不能为空' };
  }

  const trimmedName = name.trim();
  
  // 统计汉字数量
  const chineseChars = trimmedName.match(/[\u4e00-\u9fff]/g) || [];
  // 统计英文字母数量
  const englishChars = trimmedName.match(/[a-zA-Z]/g) || [];
  
  if (chineseChars.length > 0 && englishChars.length > 0) {
    return { valid: false, message: '智能体名称只能包含汉字或英文字母，不能混合使用' };
  }
  
  if (chineseChars.length > 5) {
    return { valid: false, message: '智能体名称不能超过 5 个汉字' };
  }
  
  if (englishChars.length > 10) {
    return { valid: false, message: '智能体名称不能超过 10 个英文字母' };
  }
  
  // 检查是否只包含汉字或英文字母
  const validPattern = /^[\u4e00-\u9fff a-zA-Z]+$/;
  if (!validPattern.test(trimmedName)) {
    return { valid: false, message: '智能体名称只能包含汉字或英文字母' };
  }
  
  return { valid: true };
};